import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Chip, Alert, CircularProgress,
  Paper, Tooltip, IconButton,
} from '@mui/material';
import {
  Add as AddIcon, Block as BlockIcon, Refresh as RefreshIcon,
  Delete as DeleteIcon, Event as EventIcon, CheckCircle as ConfirmIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { api, apiRoutes } from '../../api';
import TaskDetailPanel from './TaskDetailPanel';

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const EVENT_COLORS = {
  INSPECTION: '#1976d2',
  SHOWING: '#7b1fa2',
  OPEN_HOUSE: '#388e3c',
  OFF: '#757575',
  MISSION: '#f57c00',
  SURVEY: '#0097a7',
  BLACKOUT: '#d32f2f',
  CANCELLED: '#bdbdbd',
  VISIT_PENDING: '#ff9800',
};

const KIND_LABELS = {
  INSPECTION: 'بازرسی',
  SHOWING: 'بازدید خصوصی',
  OPEN_HOUSE: 'بازدید عمومی',
  OFF: 'مرخصی / تعطیل',
  MISSION: 'مأموریت',
  SURVEY: 'بررسی میدانی',
  BLACKOUT: 'تعطیل رسمی',
};

/**
 * AgentCalendar
 *
 * Normal mode : drag-to-create blocks/blackouts.
 * Visit mode  : visitMode=true — agent drags a slot, sees orange highlight +
 *               confirm bar, then calls onVisitSelected(startStr, endStr).
 */
const AgentCalendar = ({
  visitMode = false,
  taskLabel = '',
  onVisitSelected = null,
  visitingWindows = [],   // FullCalendar businessHours format from /visiting-windows/
  offeredSlots = [],      // ISO strings from the user's showing request
}) => {
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingSlot, setPendingSlot] = useState(null);

  // normal-mode dialogs
  const [createDialog, setCreateDialog] = useState({ open: false, start: null, end: null });
  const [createForm, setCreateForm] = useState({ kind: 'OFF', title: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, event: null });
  const [cancelLoading, setCancelLoading] = useState(false);

  // task detail dialog (when clicking an INSPECTION event)
  const [taskDialog, setTaskDialog] = useState({ open: false, task: null, loading: false });

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventsRes, blackoutsRes] = await Promise.all([
        api.get(apiRoutes.agentCalendar.events, { headers: authHeader() }).catch(() => ({ data: [] })),
        api.get(apiRoutes.agentCalendar.blackouts, { headers: authHeader() }).catch(() => ({ data: [] })),
      ]);

      const calEvents = [];

      (eventsRes.data.results || eventsRes.data || []).forEach((e) => {
        calEvents.push({
          id: `event-${e.id}`,
          title: e.title || KIND_LABELS[e.kind] || e.kind,
          start: e.start_at, end: e.end_at,
          backgroundColor: e.status === 'CANCELLED' ? EVENT_COLORS.CANCELLED : (EVENT_COLORS[e.kind] || '#1976d2'),
          borderColor: 'transparent',
          extendedProps: { type: 'event', raw: e },
          editable: false,
        });
      });

      (blackoutsRes.data.results || blackoutsRes.data || []).forEach((b) => {
        calEvents.push({
          id: `blackout-${b.id}`,
          title: b.reason || 'تعطیل',
          start: b.start_at, end: b.end_at,
          backgroundColor: EVENT_COLORS.BLACKOUT,
          borderColor: 'transparent',
          extendedProps: { type: 'blackout', raw: b },
          editable: false,
        });
      });

      setEvents(calEvents);
    } catch (err) {
      setError('بارگذاری تقویم ناموفق بود');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const allEvents = [
    ...events,
    // User's proposed visit times — shown as green background events
    ...(visitMode ? offeredSlots.map((slot, i) => {
      // slot can be a bare ISO string (old) or {start, end} object (new)
      const slotStart = typeof slot === 'string' ? slot : slot.start;
      const slotEnd   = typeof slot === 'string'
        ? new Date(new Date(slot).getTime() + 60 * 60 * 1000).toISOString()
        : slot.end;
      return {
        id: `offered-${i}`,
        title: `پیشنهاد کاربر ${i + 1}`,
        start: slotStart,
        end: slotEnd,
        display: 'background',
        backgroundColor: '#43a047',
        classNames: ['offered-slot-bg'],
      };
    }) : []),
    // Pending slot the agent just dragged
    ...(pendingSlot ? [{
      id: 'pending-visit',
      title: `📍 بازدید: ${taskLabel}`,
      start: pendingSlot.start, end: pendingSlot.end,
      backgroundColor: EVENT_COLORS.VISIT_PENDING,
      borderColor: '#e65100',
      editable: false,
    }] : []),
  ];

  const handleSelect = (info) => {
    if (visitMode) {
      // Validate against visiting windows if any are defined
      if (visitingWindows.length > 0) {
        const start = info.start;
        const end = info.end;
        const weekday = start.getDay(); // 0=Sun ... 6=Sat
        const startMins = start.getHours() * 60 + start.getMinutes();
        const endMins = end.getHours() * 60 + end.getMinutes();
        const dateStr = start.toISOString().slice(0, 10);

        const allowed = visitingWindows.some(w => {
          // Check weekday match
          if (w.daysOfWeek && !w.daysOfWeek.includes(weekday)) return false;
          // Check date-specific recur range
          if (w.startRecur && dateStr < w.startRecur) return false;
          if (w.endRecur && dateStr >= w.endRecur) return false;
          // Check time range
          const [wStartH, wStartM] = w.startTime.split(':').map(Number);
          const [wEndH, wEndM] = w.endTime.split(':').map(Number);
          const wStartMins = wStartH * 60 + wStartM;
          const wEndMins = wEndH * 60 + wEndM;
          return startMins >= wStartMins && endMins <= wEndMins;
        });

        if (!allowed) {
          info.view.calendar.unselect();
          return; // silently reject — calendar already shows green zones
        }
      }
      setPendingSlot({ start: info.start, end: info.end, startStr: info.startStr, endStr: info.endStr });
    } else {
      setCreateForm({ kind: 'OFF', title: '' });
      // For all-day selections FullCalendar gives bare date strings like "2026-05-06".
      // Convert them to local-midnight ISO strings so the backend stores the correct day.
      // End is exclusive midnight of next day — subtract 1s to get 23:59:59 of the selected day.
      const isAllDay = info.allDay;
      setCreateDialog({
        open: true,
        start: toLocalISO(info.start),
        end: isAllDay ? toLocalISOEndInclusive(info.end) : toLocalISO(info.end),
      });
    }
    info.view.calendar.unselect();
  };

  const handleCreateSubmit = async () => {
    setCreateLoading(true);
    try {
      await api.post(apiRoutes.agentCalendar.events,
        { title: createForm.title || KIND_LABELS[createForm.kind], kind: createForm.kind,
          start_at: createDialog.start, end_at: createDialog.end, hard_busy: true },
        { headers: authHeader() });
      setCreateDialog({ open: false, start: null, end: null });
      await loadEvents();
    } catch (err) {
      const msg = err.response?.data?.detail
        || (err.response?.status === 409 ? 'این بازه زمانی با یک رویداد موجود تداخل دارد.' : 'خطا در ایجاد رویداد');
      setError(msg);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEventClick = async (info) => {
    if (info.event.id === 'pending-visit') return;

    const { type, raw } = info.event.extendedProps || {};

    // INSPECTION event → fetch task and open TaskDetailPanel
    if (type === 'event' && raw?.kind === 'INSPECTION') {
      const taskId = raw?.meta?.task_id;
      if (!taskId) return;
      setTaskDialog({ open: true, task: null, loading: true });
      try {
        const res = await api.get(apiRoutes.inspectionTasks.detail(taskId), { headers: authHeader() });
        setTaskDialog({ open: true, task: res.data, loading: false });
      } catch (e) {
        setError('خطا در بارگذاری اطلاعات وظیفه');
        setTaskDialog({ open: false, task: null, loading: false });
      }
      return;
    }

    // everything else → generic view dialog
    setViewDialog({ open: true, event: info.event });
  };

  const handleCancelEvent = async () => {
    const { event } = viewDialog;
    const { type, raw } = event.extendedProps;
    setCancelLoading(true);
    try {
      if (type === 'blackout') {
        await api.delete(apiRoutes.agentCalendar.blackout(raw.id), { headers: authHeader() });
      } else if (type === 'event') {
        // Task-linked kinds (INSPECTION, SHOWING, OPEN_HOUSE) go through the visit cancel endpoint
        const taskLinked = ['INSPECTION', 'SHOWING', 'OPEN_HOUSE'];
        if (taskLinked.includes(raw.kind)) {
          await api.post(apiRoutes.visits.cancel(raw.id), { reason: 'لغو توسط نماینده' }, { headers: authHeader() });
        } else {
          // Simple kinds (OFF, MISSION, SURVEY) — delete directly
          await api.delete(apiRoutes.agentCalendar.event(raw.id), { headers: authHeader() });
        }
      }
      setViewDialog({ open: false, event: null });
      await loadEvents();
    } catch (err) {
      setError(err.response?.data?.detail || 'خطا در لغو رویداد');
    } finally {
      setCancelLoading(false);
    }
  };

  const fmt = (dt) => dt ? new Date(dt).toLocaleString('fa-IR', { dateStyle: 'medium', timeStyle: 'short' }) : '';

  // Convert a JS Date to a local-timezone ISO string (e.g. "2026-05-06T00:00:00+03:30").
  // This prevents all-day selections from being misinterpreted as UTC midnight on the backend.
  const toLocalISO = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    const off = -date.getTimezoneOffset(); // minutes, positive = ahead of UTC
    const sign = off >= 0 ? '+' : '-';
    const absOff = Math.abs(off);
    const offH = pad(Math.floor(absOff / 60));
    const offM = pad(absOff % 60);
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
      `${sign}${offH}:${offM}`
    );
  };

  // FullCalendar all-day end is exclusive midnight of the next day.
  // Subtract 1 second so the stored end is 23:59:59 of the selected day,
  // avoiding overlap with events that start at midnight of the following day.
  const toLocalISOEndInclusive = (date) => {
    const adjusted = new Date(date.getTime() - 1000); // -1 second
    return toLocalISO(adjusted);
  };

  return (
    <Box dir="rtl" className="agents-section agents-calendar">
      {visitMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {offeredSlots.length > 0
            ? `زمان‌های سبز رنگ پیشنهادی کاربر هستند. روی تقویم بکشید تا زمان بازدید را تأیید کنید${taskLabel ? ` — ${taskLabel}` : ''}`
            : `روی تقویم کلیک کرده و بکشید تا زمان بازدید را انتخاب کنید${taskLabel ? ` — ${taskLabel}` : ''}`
          }
        </Alert>
      )}

      {!visitMode && (
        <Box className="agents-section-head" display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h5">تقویم نماینده</Typography>
            <Typography variant="body2" color="text.secondary">برای ایجاد بلوک زمانی، روی تقویم کلیک کرده و بکشید</Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            {Object.entries(KIND_LABELS).map(([key, label]) => (
              <Chip key={key} label={label} size="small"
                sx={{ backgroundColor: EVENT_COLORS[key], color: '#fff', fontSize: 11 }} />
            ))}
            <Tooltip title="تازه‌سازی">
              <IconButton onClick={loadEvents} disabled={loading}><RefreshIcon /></IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Pending slot confirm bar */}
      {visitMode && pendingSlot && (
        <Paper elevation={3} sx={{ p: 2, mb: 2, border: '2px solid #ff9800', borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="subtitle2">زمان انتخاب‌شده:</Typography>
              <Typography variant="body2">از {fmt(pendingSlot.start)} تا {fmt(pendingSlot.end)}</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button variant="outlined" color="inherit" size="small" onClick={() => setPendingSlot(null)}>تغییر</Button>
              <Button variant="contained" color="success" size="small" startIcon={<ConfirmIcon />}
                onClick={() => onVisitSelected?.(pendingSlot.startStr, pendingSlot.endStr)}>
                تأیید این زمان
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {loading && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}

      {!loading && (
        <Paper sx={{ p: 1 }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
            locale="fa"
            direction="rtl"
            firstDay={6}
            slotDuration="01:00:00"
            height="auto"
            selectable
            selectMirror
            dayMaxEvents
            events={allEvents}
            select={handleSelect}
            eventClick={handleEventClick}
            slotMinTime="07:00:00"
            slotMaxTime="22:00:00"
            allDaySlot
            nowIndicator
            businessHours={
              visitMode && visitingWindows.length > 0
                ? visitingWindows
                : { daysOfWeek: [0, 1, 2, 3, 4, 6], startTime: '08:00', endTime: '20:00' }
            }
            selectConstraint={visitMode && visitingWindows.length > 0 ? 'businessHours' : undefined}
            buttonText={{ today: 'امروز', month: 'ماه', week: 'هفته', day: 'روز' }}
          />
        </Paper>
      )}

      {/* Create event dialog (normal mode) */}
      <Dialog open={createDialog.open} onClose={() => setCreateDialog({ open: false, start: null, end: null })} maxWidth="xs" fullWidth>
        <DialogTitle><Box display="flex" alignItems="center" gap={1}><AddIcon />ایجاد رویداد جدید</Box></DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField select label="نوع رویداد" value={createForm.kind}
              onChange={(e) => setCreateForm(f => ({ ...f, kind: e.target.value }))} fullWidth size="small">
              <MenuItem value="OFF">
                <Box display="flex" alignItems="center" gap={1}>
                  <BlockIcon fontSize="small" sx={{ color: EVENT_COLORS.OFF }} />مرخصی / تعطیل
                </Box>
              </MenuItem>
              <MenuItem value="MISSION">
                <Box display="flex" alignItems="center" gap={1}>
                  <EventIcon fontSize="small" sx={{ color: EVENT_COLORS.MISSION }} />مأموریت
                </Box>
              </MenuItem>
              <MenuItem value="SURVEY">
                <Box display="flex" alignItems="center" gap={1}>
                  <EventIcon fontSize="small" sx={{ color: EVENT_COLORS.SURVEY }} />بررسی میدانی
                </Box>
              </MenuItem>
            </TextField>
            <TextField label="عنوان (اختیاری)" value={createForm.title}
              onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
              fullWidth size="small" placeholder={KIND_LABELS[createForm.kind]} />
            <Typography variant="caption" color="text.secondary">
              از {fmt(createDialog.start)} تا {fmt(createDialog.end)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog({ open: false, start: null, end: null })}>لغو</Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={createLoading}>
            {createLoading ? <CircularProgress size={18} /> : 'ذخیره'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View/cancel event dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, event: null })} maxWidth="xs" fullWidth>
        <DialogTitle>{viewDialog.event?.title}</DialogTitle>
        <DialogContent>
          {viewDialog.event && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2">شروع: {fmt(viewDialog.event.start)}</Typography>
              {viewDialog.event.end && <Typography variant="body2">پایان: {fmt(viewDialog.event.end)}</Typography>}
              <Chip label={viewDialog.event.extendedProps?.raw?.status || viewDialog.event.extendedProps?.type}
                size="small" sx={{ alignSelf: 'flex-start', mt: 1 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, event: null })}>بستن</Button>
          {viewDialog.event?.extendedProps?.type !== 'task' && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={handleCancelEvent} disabled={cancelLoading}>
              {cancelLoading ? <CircularProgress size={18} /> : 'لغو / حذف'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Task detail dialog — opened when clicking an INSPECTION event */}
      <Dialog
        open={taskDialog.open}
        onClose={() => { setTaskDialog({ open: false, task: null, loading: false }); loadEvents(); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">جزئیات وظیفه بازرسی</Typography>
            <IconButton onClick={() => { setTaskDialog({ open: false, task: null, loading: false }); loadEvents(); }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {taskDialog.loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          )}
          {!taskDialog.loading && taskDialog.task && (
            <TaskDetailPanel
              task={taskDialog.task}
              onBack={() => { setTaskDialog({ open: false, task: null, loading: false }); loadEvents(); }}
              onTaskUpdated={loadEvents}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AgentCalendar;
